import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

/**
 * Horizontal 11-step pipeline indicator.
 * When `stageHistory` is provided, only stages that were actually reached are
 * marked completed — stages skipped by an early client decision (e.g. the CTH
 * interpretive-report stages 8-10) stay muted instead of showing false checks.
 */
export function RecruitmentStepper({ currentStage = 1, compact = false, stageHistory = null }) {
  const { t } = useTranslation();
  const stages = Array.from({ length: 11 }, (_, i) => i + 1);
  const reached = Array.isArray(stageHistory) && stageHistory.length
    ? new Set(stageHistory.map((h) => Number(h?.stage ?? h)).filter((n) => !Number.isNaN(n)))
    : null;

  const isDone = (s) => (reached
    ? reached.has(s) && (s < currentStage || (s === 11 && currentStage >= 11))
    : s < currentStage || (s === 11 && currentStage >= 11));
  const isSkipped = (s) => Boolean(reached && !reached.has(s) && s < currentStage && currentStage >= 11);
  const skippedReportStages = [8, 9, 10].some((s) => isSkipped(s));

  return (
    <div className="w-full" data-testid="recruitment-stepper">
      <p className="text-xs text-muted-foreground mb-2 tabular-nums">
        {t('recruitment.stepperOf', { n: currentStage })}
      </p>
      <div className="flex items-center gap-1 overflow-x-auto pb-2">
        {stages.map((s, idx) => {
          const done = isDone(s);
          const skipped = isSkipped(s);
          const isActive = s === currentStage && !done;
          const dotCls = done
            ? 'bg-success text-white border-success'
            : isActive
              ? 'bg-primary text-primary-foreground border-primary shadow-md shadow-primary/30'
              : 'bg-muted text-muted-foreground border-border';
          const lineDone = reached
            ? done && (isDone(s + 1) || s + 1 === currentStage)
            : s < currentStage || currentStage >= 11;
          const suffix = isActive ? '-active' : done ? '-done' : skipped ? '-skipped' : '';
          return (
            <React.Fragment key={s}>
              <div
                className="flex flex-col items-center min-w-[78px]"
                data-testid={`recruitment-stage-${s}${suffix}`}
              >
                <div
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs font-semibold tabular-nums transition-colors ${dotCls}`}
                >
                  {done ? <Check className="h-3.5 w-3.5" /> : s}
                </div>
                {!compact && (
                  <p className={`mt-1.5 text-xs leading-tight text-center max-w-[78px] ${isActive ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                    {t(`recruitment.stages.${s}`)}
                  </p>
                )}
              </div>
              {idx < stages.length - 1 && (
                <div className={`h-0.5 flex-1 min-w-[14px] ${lineDone ? 'bg-success' : 'bg-border'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {skippedReportStages && (
        <p className="text-xs text-muted-foreground italic" data-testid="recruitment-stepper-parallel-note">
          {t('recruitment.stepperParallelNote', { defaultValue: 'The process was closed by a client hiring decision. The CTH interpretive report follows its own review, quality-control and release cycle separately.' })}
        </p>
      )}
    </div>
  );
}

export default RecruitmentStepper;
